// Geometry for the issuing-analytics charts.
//
// There is no chart library in this app and there isn't going to be one: every
// chart on the issuing page is divs positioned by percentages. So ALL of the
// arithmetic lives here and the components read as markup — the same split as
// `lib/gestogram.ts` / `consult/Gestogram.tsx`.
//
// Two rules hold across every function:
//
//   1. Percentages come back rounded to one decimal place. That is finer than a
//      pixel on any plot we draw, and it keeps `width: 33.333333333333336%` out
//      of the DOM.
//   2. A degenerate series never produces `NaN`, `Infinity` or a negative size.
//      An empty series, an all-zero series and a single reading are the shapes
//      that turn a hand-rolled chart into a broken box, so each is handled
//      deliberately and named in the tests.

/** The vocabulary the charts render: a member of a series with a value. */
export interface ChartValue {
  readonly id: string;
  readonly value: number;
}

/** A `ChartValue` carrying already-resolved display text. */
export interface ChartMember extends ChartValue {
  readonly label: string;
}

/** One time bucket of the trend panel: the segments stacked inside a column. */
export interface ChartBucket {
  readonly id: string;
  readonly segments: readonly ChartValue[];
  /**
   * True when the window covers only part of this bucket — carried straight
   * through from `PanelMember.partial`, because it is a fact about the data and
   * not about the drawing. The geometry does nothing with it: a partial bucket's
   * total is a real total and scales like any other. It exists so the component
   * can say the column is not comparable to its neighbours.
   */
  readonly partial?: boolean;
}

export interface StackedSegment extends ChartValue {
  /** Distance from the plot floor to this segment's base, in percent. */
  readonly offsetPct: number;
  /** This segment's own height, in percent of the plot. */
  readonly sizePct: number;
}

export interface StackedColumn {
  readonly id: string;
  /** Bottom-up, zero-value segments omitted (see `stackBuckets`). */
  readonly segments: readonly StackedSegment[];
}

export interface StackedColumns {
  /** The tallest bucket total — the value the plot ceiling represents. */
  readonly max: number;
  /** One column per input bucket, in input order. */
  readonly columns: readonly StackedColumn[];
}

export type DeltaDirection = 'up' | 'down' | 'flat';

export interface ChartDelta {
  readonly absolute: number;
  /**
   * Whole percent change, or `null` when the baseline was too small for a
   * percentage to mean anything — see `MIN_PERCENT_BASELINE`. Callers render the
   * absolute change in that case.
   */
  readonly percent: number | null;
  readonly direction: DeltaDirection;
}

/**
 * Below this previous-period value a percentage change is noise, not signal: a
 * baseline of 1 turns one extra issue into "+100%", which reads as a trend when
 * it is a rounding error in a small clinic's day. At 10 the worst a single unit
 * can claim is 10%, which is a fair thing to print. Under the threshold the
 * caller shows the absolute change, which is honest at any size.
 */
const MIN_PERCENT_BASELINE = 10;

/**
 * The smallest bar `scaleToMax` will draw for a value that isn't zero. Below a
 * 1:1000 ratio the honest percentage rounds to `0` and the row renders as no bar
 * at all, sitting next to a figure that says something *was* issued — the one
 * reading the chart must never give. A percent is a visible sliver on any track
 * we draw and cannot be mistaken for a competitive row, and the exact figure is
 * text on the same line either way.
 */
const MIN_VISIBLE_PCT = 1;

/** One decimal place — see the rounding note at the top of the file. */
function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * Every measure that reaches these charts is a non-negative count. A negative
 * (a server bug) or a non-finite (a bad parse) is floored to zero rather than
 * drawn as an inverted or `NaN`-sized bar — a chart is the wrong place to
 * discover a data fault, and a size of `NaN%` takes the whole panel down.
 */
function magnitude(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

/** Largest magnitude in the series, or 0 for an empty/all-zero one. */
function peak(values: readonly number[]): number {
  return values.reduce((max, v) => (v > max ? v : max), 0);
}

/**
 * Each value as a percentage of the series maximum — the bar widths of a ranked
 * "share of leader" list, where the top row is always full-width.
 *
 * Empty series → empty result. All-zero series → all zeros: nothing is drawn,
 * which is the truth, rather than every row claiming to lead. A value that *is*
 * non-zero always draws, floored at `MIN_VISIBLE_PCT`, however far behind the
 * leader it is.
 */
export function scaleToMax(values: readonly number[]): number[] {
  const safe = values.map(magnitude);
  const max = peak(safe);
  if (max === 0) return safe.map(() => 0);
  return safe.map((v) => (v === 0 ? 0 : Math.max(round1((v / max) * 100), MIN_VISIBLE_PCT)));
}

/**
 * Each value as a percentage of a total the caller states — the ranked list's
 * "% of all issuing" readout.
 *
 * The denominator has to be passed in rather than summed from `values`, because
 * that list is a server-truncated top N: its own sum is the top ten's issuing,
 * not the period's. Dividing by it would report the leading item of a 200-item
 * clinic as 30% when it is 12%, and would make any ten rows add to exactly 100%.
 *
 * A zero (or missing) total gives every value a zero share rather than `NaN`:
 * nothing was issued, so nothing has a share of it.
 */
export function shareOf(values: readonly number[], total: number): number[] {
  const safe = values.map(magnitude);
  const whole = magnitude(total);
  if (whole === 0) return safe.map(() => 0);
  return safe.map((v) => round1((v / whole) * 100));
}

/**
 * Each value as a percentage of the series total — the mix bar's segment widths,
 * where the segments genuinely are the whole of the period.
 *
 * Shares are rounded independently, so they can sum to 99.9 rather than 100.
 * That is why the mix bar sizes its segments with `flex-grow` (proportional, and
 * always exactly filling) instead of literal widths.
 */
export function shareOfTotal(values: readonly number[]): number[] {
  const safe = values.map(magnitude);
  return shareOf(
    safe,
    safe.reduce((sum, v) => sum + v, 0),
  );
}

/**
 * Position the segments of every bucket against a shared ceiling — the tallest
 * bucket total — so columns are comparable across the series.
 *
 * `offsetPct`/`sizePct` are percentages of the whole plot, not of the column, so
 * a segment is one absolutely-positioned div and the column needs no height of
 * its own. Boundaries are rounded first and sizes derived from them, so adjacent
 * segments meet exactly instead of leaving a rounding seam.
 *
 * Zero-value segments are dropped: they have nothing to draw, and an empty div
 * still shows its rounding and any border. A bucket with no issuing therefore
 * comes back with no segments at all — the caller renders the column anyway, as
 * the gap that says "this day happened and was quiet".
 */
export function stackBuckets(buckets: readonly ChartBucket[]): StackedColumns {
  const max = peak(
    buckets.map((bucket) =>
      bucket.segments.reduce((sum, segment) => sum + magnitude(segment.value), 0),
    ),
  );

  const columns = buckets.map((bucket) => {
    const segments: StackedSegment[] = [];
    let cursor = 0;

    for (const segment of bucket.segments) {
      const value = magnitude(segment.value);
      // Skipping zeros also guards the division: reaching it at all means some
      // bucket had a positive total, so `max` is positive.
      if (value === 0) continue;
      const offsetPct = round1((cursor / max) * 100);
      cursor += value;
      segments.push({
        id: segment.id,
        value,
        offsetPct,
        sizePct: round1(round1((cursor / max) * 100) - offsetPct),
      });
    }

    return { id: bucket.id, segments };
  });

  return { max, columns };
}

/**
 * Period-over-period change for the headline tiles. `percent` is present only
 * when the baseline can carry one (see `MIN_PERCENT_BASELINE`); `direction` is
 * separated out so the tile picks its arrow and tone without doing arithmetic.
 */
export function computeDelta(current: number, previous: number): ChartDelta {
  const now = magnitude(current);
  const before = magnitude(previous);
  const absolute = now - before;

  return {
    absolute,
    percent: before >= MIN_PERCENT_BASELINE ? Math.round((absolute / before) * 100) : null,
    direction: absolute > 0 ? 'up' : absolute < 0 ? 'down' : 'flat',
  };
}

// `sparklinePoints` used to live here, feeding a per-row trend line in the
// ranked list. Both were deleted: no panel in `@synapse/analytics-contract`
// groups by `[time_bucket, item]`, so there was never a per-item series to plot.
// Bringing it back is one new panel spec in `packages/analytics-contract/src/
// panels.ts` and nothing else here — but the ranked list's rows already filter
// the whole page to one medicine, which answers "this medicine over time" with
// the full trend chart rather than with a twelve-pixel line.
